import { templateSchema as schema, text, pageUrl } from "./template-pages.mjs";
export const componentTypes = {
  banner: "广告横幅",
  text: "文字内容",
  image: "图片展示",
  "page-link": "页面入口",
  "video-grid": "视频列表",
  builtin: "系统区块",
};
export const builtinDependencies = {
  "home:header": ["search"],
  "discover:header": ["search"],
  "discover:videos": ["home"],
  "profile:cover": ["collection", "settings"],
  "profile:info": ["collection"],
  "profile:videos": ["home"],
  "messages:header": ["settings"],
  "search:results": ["home"],
  "collection:content": ["home"],
  "create:camera": ["publish"],
  "publish:footer": ["home", "profile"],
};
export function defaultComponents() {
  return [
    ...schema.pages.flatMap((p) =>
      p.blocks.map((b) => ({
        id: `CMP-${p.id}-${b.id}`,
        name: `${p.name} / ${b.name}`,
        type: "builtin",
        renderer: `${p.id}:${b.id}`,
        status: "启用",
        params: {},
      })),
    ),
    {
      id: "CMP-BANNER",
      name: "广告横幅",
      type: "banner",
      renderer: "",
      status: "启用",
      params: {
        title: "发现你的下一部好剧",
        imageUrl: "/media/lake.jpg",
        url: "#discover",
        radius: 12,
      },
    },
    {
      id: "CMP-TEXT",
      name: "文字内容",
      type: "text",
      renderer: "",
      status: "启用",
      params: { title: "精彩内容", text: "在组件管理中编辑展示内容。", padding: 16 },
    },
    {
      id: "CMP-VIDEO-GRID",
      name: "视频列表",
      type: "video-grid",
      renderer: "",
      status: "启用",
      params: { title: "推荐视频", columns: 2, gap: 12 },
    },
    {
      id: "CMP-PAGE-LINK",
      name: "页面入口",
      type: "page-link",
      renderer: "",
      status: "启用",
      params: { title: "返回首页", url: "#home", padding: 16 },
    },
  ];
}
export function normalizeComponents(value) {
  return (Array.isArray(value) ? value : defaultComponents()).slice(0, 300).map((raw, i) => {
    const p = raw?.params || {};
    return {
      id: text(raw?.id, `CMP-${i + 1}`),
      name: text(raw?.name, "新组件"),
      type: Object.hasOwn(componentTypes, raw?.type) ? raw.type : "text",
      renderer: text(raw?.renderer),
      status: raw?.status === "停用" ? "停用" : "启用",
      params: {
        title: text(p.title),
        text: text(p.text, "", 4000),
        imageUrl: text(p.imageUrl, "", 2000),
        url: text(p.url, "", 2000),
        gap: Math.min(48, Math.max(0, Number(p.gap) || 0)),
        padding: Math.min(48, Math.max(0, Number(p.padding) || 0)),
        radius: Math.min(32, Math.max(0, Number(p.radius) || 0)),
        color: /^#[a-f0-9]{6}$/i.test(p.color) ? p.color : "",
        background: /^#[a-f0-9]{6}$/i.test(p.background) ? p.background : "",
        columns: [1, 2, 3].includes(p.columns) ? p.columns : 2,
      },
    };
  });
}
export function normalizeBottomMenus(value, templates) {
  const menus = Array.isArray(value)
    ? value
    : templates.map((t) => ({
        id: t.id === "TPL-DEFAULT" ? "BM-DEFAULT" : `BM-${t.id}`,
        name: `${t.name}底部菜单`,
        status: "启用",
        items: t.pages
          .filter((p) => p.enabled && p.navigation)
          .map((p) => ({
            id: `BMI-${p.id}`,
            name: p.name,
            url: p.url,
            icon: p.id === "create" ? "plus" : "",
            status: "启用",
            sort: p.sort,
          })),
      }));
  return menus.slice(0, 100).map((m, i) => ({
    id: text(m?.id, `BM-${i + 1}`),
    name: text(m?.name, "底部菜单"),
    templateIds: templates.filter((t) => t.bottomMenuId === m?.id).map((t) => t.id),
    status: m?.status === "停用" ? "停用" : "启用",
    items: (Array.isArray(m?.items) ? m.items : [])
      .slice(0, 8)
      .map((item, j) => ({
        id: text(item?.id, `BMI-${j + 1}`),
        name: text(item?.name, "菜单"),
        url: pageUrl(item?.url),
        icon: text(item?.icon),
        status: item?.status === "停用" ? "停用" : "启用",
        sort: Math.max(1, Number(item?.sort) || j + 1),
      }))
      .sort((a, b) => a.sort - b.sort),
  }));
}
// Template bindings remain authoritative; editing a shared bottom menu updates
// all selected templates atomically, without touching other groups' bindings.
export function setBottomMenuTemplates(config, menuId, templateIds) {
  const selected = new Set(templateIds);
  const templates = config.templates.map((t) => ({
    ...t,
    bottomMenuId: selected.has(t.id) ? menuId : t.bottomMenuId === menuId ? "" : t.bottomMenuId,
  }));
  return { ...config, templates, bottomMenus: normalizeBottomMenus(config.bottomMenus, templates) };
}
export function getBottomMenuPages(config, templateIds) {
  const templates = config.templates.filter((t) => templateIds.includes(t.id));
  if (!templates.length) return [];
  return templates[0].pages.filter(
    (p) =>
      p.enabled &&
      templates.every((t) => t.pages.some((other) => other.enabled && other.url === p.url)),
  );
}
export function completeTemplatePlatform(config, raw) {
  const templates = config.templates.map((t) => ({
    ...t,
    bottomMenuId: text(
      raw?.templates?.find((r) => r.id === t.id)?.bottomMenuId,
      Array.isArray(raw?.bottomMenus) ? "" : t.id === "TPL-DEFAULT" ? "BM-DEFAULT" : `BM-${t.id}`,
    ),
  }));
  return {
    ...config,
    revision: Math.max(0, Number(raw?.revision) || 0),
    templates,
    components: normalizeComponents(raw?.components),
    bottomMenus: normalizeBottomMenus(raw?.bottomMenus, templates),
  };
}
export function getComponentReferences(config, id) {
  return config.templates.flatMap((t) =>
    t.pages.flatMap((p) =>
      p.blocks
        .filter((b) => b.componentId === id)
        .map((b) => `模板“${t.name}” / 页面“${p.name}” / 区块“${b.id}”`),
    ),
  );
}
export function getBottomMenuReferences(config, id) {
  return config.templates.filter((t) => t.bottomMenuId === id).map((t) => `模板“${t.name}”`);
}
export function getPageReferences(config, templateId, pageId) {
  const t = config.templates.find((t) => t.id === templateId),
    target = t?.pages.find((p) => p.id === pageId);
  if (!target) return [];
  const refs = [],
    menu = config.bottomMenus.find((m) => m.id === t.bottomMenuId);
  for (const item of menu?.items || [])
    if (item.url === target.url) refs.push(`底部菜单“${menu.name}” / “${item.name}”`);
  for (const p of t.pages) {
    if (p.id === pageId) continue;
    for (const b of p.blocks) {
      const c = config.components.find((c) => c.id === b.componentId);
      if (
        c?.params.url === target.url ||
        (c?.type === "video-grid" && target.id === "home") ||
        (c?.type === "builtin" &&
          p.enabled &&
          b.enabled &&
          (builtinDependencies[c.renderer] || []).includes(target.id))
      )
        refs.push(`页面“${p.name}” / 组件“${c.name}”`);
    }
  }
  return [...new Set(refs)];
}
const safeLink = (v) =>
  !v ||
  !!pageUrl(v) ||
  (/^https?:\/\//i.test(v) &&
    (() => {
      try {
        return ["http:", "https:"].includes(new URL(v).protocol);
      } catch {
        return false;
      }
    })());
export function validateTemplatePlatform(config, previous = null) {
  const errors = [],
    unique = (rows, label) => {
      if (new Set(rows.map((r) => r.id)).size !== rows.length) errors.push(`${label} ID 不能重复`);
    };
  unique(config.components, "组件");
  unique(config.bottomMenus, "底部菜单");
  for (const c of config.components) {
    if (!safeLink(c.params.url)) errors.push(`组件“${c.name}”链接须为页面 #URL 或 HTTP/HTTPS URL`);
    if (c.params.imageUrl && !/^(https?:\/\/|\/(?!\/))/.test(c.params.imageUrl))
      errors.push(`组件“${c.name}”图片地址无效`);
    if (
      c.type === "builtin" &&
      !schema.pages.some((p) => p.blocks.some((b) => `${p.id}:${b.id}` === c.renderer))
    )
      errors.push(`组件“${c.name}”系统区块无效`);
    if (c.status === "停用" && getComponentReferences(config, c.id).length)
      errors.push(
        `组件“${c.name}”被引用，不能隐藏：${getComponentReferences(config, c.id).join("；")}`,
      );
  }
  for (const m of config.bottomMenus) {
    unique(m.items, `底部菜单“${m.name}”的菜单项`);
    if (!m.items.length) errors.push(`底部菜单“${m.name}”至少需要一个菜单项`);
    if (m.items.some((item) => !item.url)) errors.push(`底部菜单“${m.name}”只能关联页面 #URL`);
    if (m.status === "停用" && getBottomMenuReferences(config, m.id).length)
      errors.push(`底部菜单“${m.name}”被模板引用，不能隐藏`);
  }
  for (const t of config.templates) {
    unique(t.pages, `模板“${t.name}”页面`);
    if (new Set(t.pages.map((p) => p.url)).size !== t.pages.length)
      errors.push(`模板“${t.name}”页面 URL 不能重复`);
    for (const page of t.pages) {
      if (t.pages.some((other) => other.id !== page.id && page.url === `#${other.id}`))
        errors.push(`模板“${t.name}”页面 URL ${page.url} 与其他页面标识冲突，请使用其他名称`);
    }
    const available = (url) => t.pages.some((p) => p.url === url && p.enabled),
      menu = config.bottomMenus.find((m) => m.id === t.bottomMenuId);
    if (t.bottomMenuId && (!menu || menu.status === "停用"))
      errors.push(`模板“${t.name}”引用的底部菜单不存在或已隐藏`);
    for (const item of menu?.items || [])
      if (!available(item.url))
        errors.push(`模板“${t.name}” / 底部菜单“${item.name}”指向不存在或隐藏的页面 ${item.url}`);
    for (const p of t.pages) {
      unique(p.blocks, `页面“${p.name}”区块`);
      for (const b of p.blocks) {
        const c = config.components.find((c) => c.id === b.componentId);
        if (!c || c.status === "停用") {
          errors.push(
            `模板“${t.name}” / 页面“${p.name}”引用的组件 ${b.componentId} 不存在或已隐藏`,
          );
          continue;
        }
        if (c.type === "builtin" && c.renderer !== `${p.id}:${b.id}`)
          errors.push(`页面“${p.name}”区块“${b.id}”不能调用其他系统区块`);
        const locked = schema.pages
          .find((base) => base.id === p.id)
          ?.blocks.find((base) => base.id === b.id)?.locked;
        if (locked && c.type !== "builtin")
          errors.push(`页面“${p.name}”必要区块“${b.id}”必须使用对应系统组件`);
        if (
          c.params.url.startsWith("#") &&
          !available(c.params.url) &&
          !(c.params.url === p.url && !p.enabled)
        )
          errors.push(
            `模板“${t.name}” / 页面“${p.name}” / 组件“${c.name}”链接 ${c.params.url} 不存在或已隐藏`,
          );
        const targets =
          c.type === "video-grid"
            ? ["home"]
            : c.type === "builtin" && p.enabled && b.enabled
              ? builtinDependencies[c.renderer] || []
              : [];
        for (const target of targets)
          if (!t.pages.some((p) => p.id === target && p.enabled))
            errors.push(`页面“${p.name}” / 组件“${c.name}”关联页面 #${target} 不存在或已隐藏`);
      }
      if (!p.enabled) {
        const refs = getPageReferences(config, t.id, p.id);
        if (refs.length) errors.push(`页面“${p.name}”被引用，不能隐藏：${refs.join("；")}`);
      }
    }
  }
  if (previous) {
    for (const c of previous.components)
      if (
        !config.components.some((n) => n.id === c.id) &&
        getComponentReferences(config, c.id).length
      )
        errors.push(`组件“${c.name}”被页面引用，不能删除`);
    for (const m of previous.bottomMenus)
      if (
        !config.bottomMenus.some((n) => n.id === m.id) &&
        getBottomMenuReferences(config, m.id).length
      )
        errors.push(`底部菜单“${m.name}”被模板引用，不能删除`);
  }
  return [...new Set(errors)];
}
